import { useEffect, useMemo, useState } from "react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Search, Star, Sparkles, ShieldAlert, TrendingUp, Users, Link2, Mail, Tag, FileText, RefreshCw } from "lucide-react";
import AICreditMemoModal from "../../components/AICreditMemoModal";
import { SCORE_SIGNAL_LABELS, SCORE_WEIGHTED_KEYS } from "../../lib/scoreLabels";

// v3 score bands: Excellent 780+ · Very Good 740 · Good 700 · Fair 640 · Building 580 · High risk <580.
const scoreColor = (s) =>
  s >= 780 ? "text-emerald-600"
    : s >= 740 ? "text-emerald-500"
      : s >= 700 ? "text-foreground"
        : s >= 640 ? "text-amber-600"
          : s >= 580 ? "text-orange-600"
            : "text-rose-600";

// Friendly labels for v5.2 signal keys — used in the hover tooltip on each
// bar. Sourced from /lib/scoreLabels.js so all 3 portals stay in sync.
const SIGNAL_LABEL = SCORE_SIGNAL_LABELS;

// Admin oversight: every buyer with their verqo Score v3 (10 signals, hard
// caps, bonuses, credit utilization). Single source of truth admins use to
// spot-check the engine + activity.
//
// Iter-82 (May 2026): added a second top-level tab "Relationships" so admin
// can audit every seller↔buyer connection, invite, tag, and AR-statement
// email across the platform — closing the seller-side buyers-section
// visibility gap.
export default function AdminBuyers() {
  const [tab, setTab] = useState("scoring");
  return (
    <>
      <PageHeader title="Buyers · Admin oversight"
        subtitle="Scoring engine v3 plus cross-tenant relationships, invites, tags & statement-email audit."/>
      <div className="px-8 pt-4">
        <div data-testid="adm-buyers-tabs" className="inline-flex items-center gap-1 rounded-full bg-secondary/40 border border-border p-1">
          <TabBtn id="scoring" active={tab==="scoring"} onClick={() => setTab("scoring")} icon={Star}>Scoring engine</TabBtn>
          <TabBtn id="relationships" active={tab==="relationships"} onClick={() => setTab("relationships")} icon={Users}>Relationships</TabBtn>
        </div>
      </div>
      {tab === "scoring" && <ScoringPanel/>}
      {tab === "relationships" && <RelationshipsPanel/>}
    </>
  );
}

function TabBtn({ id, active, onClick, icon: Icon, children }) {
  return (
    <button
      data-testid={`adm-buyers-tab-${id}`}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-full transition-colors ${
        active ? "bg-white border border-border shadow-sm text-foreground" : "text-foreground/60 hover:text-foreground"
      }`}
    >
      <Icon size={13}/>
      {children}
    </button>
  );
}


// ─── Scoring engine (the legacy AdminBuyers content · unchanged) ────────
function ScoringPanel() {
  const [buyers, setBuyers] = useState([]);
  const [search, setSearch] = useState("");
  const [memoFor, setMemoFor] = useState(null);

  useEffect(() => { api.get("/admin/buyers").then(r => setBuyers(r.data)); }, []);

  const filtered = useMemo(() => {
    if (!search) return buyers;
    const q = search.toLowerCase();
    return buyers.filter(b => JSON.stringify(b).toLowerCase().includes(q));
  }, [buyers, search]);

  const avgScore = buyers.length ? Math.round(buyers.reduce((s, b) => s + (b.score || 0), 0) / buyers.length) : 0;
  const totalCreditDeployed = buyers.reduce((s, b) => s + (b.credit_limit || 0), 0);
  const totalVolume = buyers.reduce((s, b) => s + (b.total_volume || 0), 0);
  const cappedCount = buyers.filter(b => (b.caps_applied || []).length > 0).length;

  return (
    <>
      <div className="p-8 space-y-6">
        <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">
          {buyers.length} buyers · avg score {avgScore} · 10-signal credit-weighted engine · {cappedCount} currently capped
        </div>
        {/* Top tiles — v3 adds capped count */}
        <div className="grid md:grid-cols-5 gap-3">
          {[
            { l: "Total buyers", v: buyers.length },
            { l: "Avg Trade Score", v: avgScore },
            { l: "Credit limit issued", v: money(totalCreditDeployed) },
            { l: "Lifetime invoiced", v: money(totalVolume) },
            { l: "Hard-capped buyers", v: cappedCount, tone: cappedCount > 0 ? "rose" : null },
          ].map((t, i) => (
            <div key={i} className="rounded-2xl bg-white border border-border p-5">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-foreground/50">{t.l}</div>
              <div className={`display text-2xl mt-1.5 ${t.tone === "rose" ? "text-rose-700" : ""}`}>{t.v}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, company…" data-testid="admin-buyers-search"
              className="pl-9 h-10 w-72"/>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="admin-buyers-table">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
              <tr>
                <th className="px-5 py-3 font-medium">Buyer</th>
                <th className="px-5 py-3 font-medium">Score</th>
                <th className="px-5 py-3 font-medium">10-signal bars</th>
                <th className="px-5 py-3 font-medium">Caps / bonuses</th>
                <th className="px-5 py-3 font-medium">Credit limit</th>
                <th className="px-5 py-3 font-medium">Util</th>
                <th className="px-5 py-3 font-medium">Rate</th>
                <th className="px-5 py-3 font-medium">Volume</th>
                <th className="px-5 py-3 font-medium">Overdue</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(b => {
                const utilPct = b.breakdown?.credit_utilization?.utilization_pct ?? null;
                const utilBand = b.breakdown?.credit_utilization?.utilization_band;
                const utilTone = utilBand === "maxed_out" ? "text-rose-700"
                  : utilBand === "stretched" ? "text-amber-700"
                  : utilBand === "low" || utilBand === "healthy" ? "text-emerald-700"
                  : "text-foreground/60";
                return (
                  <tr key={b.id} data-testid={`adm-buyer-${b.id.slice(0, 6)}`} className="hover:bg-secondary/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-medium">{b.company || b.name}</div>
                      <div className="text-[11px] text-foreground/55">{b.email}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className={`display text-2xl ${scoreColor(b.score)}`}>{b.score}</div>
                      <div className="text-[10px] uppercase tracking-widest text-foreground/55">{b.rating}</div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1" data-testid={`adm-sigs-${b.id}`}>
                        {/* v5.2 · render ONLY the 10 weighted buckets so the
                            sparkline reflects what actually drives the score.
                            KYB + Identity moved to Stage-1 Gate (not weighted). */}
                        {SCORE_WEIGHTED_KEYS
                          .filter(k => (b.signals || {})[k] !== undefined)
                          .map(k => {
                            const v = (b.signals || {})[k];
                            return (
                              <div key={k} title={`${SIGNAL_LABEL[k] || k.replace(/_/g, ' ')}: ${v}`}
                                   className="w-1.5 h-7 rounded-full bg-secondary overflow-hidden flex items-end">
                                <div className={`w-full ${v >= 75 ? "bg-emerald-600" : v >= 50 ? "bg-foreground" : v >= 25 ? "bg-amber-500" : "bg-rose-500"}`}
                                     style={{ height: `${Math.max(v, 3)}%` }}/>
                              </div>
                            );
                          })}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1">
                        {(b.caps_applied || []).slice(0, 2).map((c, i) => (
                          <span key={`c${i}`} title={c}
                            className="inline-flex items-center gap-1 text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-1.5 py-0.5">
                            <ShieldAlert className="w-2.5 h-2.5"/>{c.split(" → ")[0].slice(0, 22)}
                          </span>
                        ))}
                        {(b.bonuses_applied || []).slice(0, 2).map((bn, i) => (
                          <span key={`b${i}`} title={bn}
                            className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5">
                            <TrendingUp className="w-2.5 h-2.5"/>{bn.split(" → ")[0].slice(0, 22)}
                          </span>
                        ))}
                        {(b.caps_applied || []).length === 0 && (b.bonuses_applied || []).length === 0 && (
                          <span className="text-[10px] text-foreground/35">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 display">{money(b.credit_limit)}</td>
                    <td className={`px-5 py-4 mono text-xs ${utilTone}`}>
                      {utilPct == null ? "—" : `${utilPct}%`}
                    </td>
                    <td className="px-5 py-4 mono">{((b.rate || 0) * 100).toFixed(2)}%</td>
                    <td className="px-5 py-4 display">{money(b.total_volume)}</td>
                    <td className="px-5 py-4">
                      {(b.overdue_count || 0) > 0
                        ? <span className="chip bg-rose-50 text-rose-700 border border-rose-200">{b.overdue_count}</span>
                        : <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200"><Star className="w-3 h-3"/>0</span>}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => setMemoFor(b)} data-testid={`ai-memo-${b.id.slice(0, 6)}`}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-foreground/65 hover:text-foreground border border-border rounded-full px-2.5 py-1 hover:bg-secondary transition-all">
                        <Sparkles className="w-3 h-3 text-accent"/>AI memo
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-foreground/55">No buyers match your search</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AICreditMemoModal buyer={memoFor} onClose={() => setMemoFor(null)}/>
    </>
  );
}


// ─── Relationships panel — iter-82 (May 2026) ───────────────────────────
// Cross-tenant audit of seller↔buyer connections, invites, tags, and
// statement emails. Surfaces every seller-side action against buyers so
// admin has 1:1 visibility for support + compliance.
function RelationshipsPanel() {
  const [view, setView] = useState("connections");
  const SUB = [
    { id: "connections", label: "Connections", icon: Link2 },
    { id: "invites",     label: "Invites",     icon: Mail },
    { id: "tags",        label: "Buyer tags",  icon: Tag },
    { id: "statements",  label: "Statement emails", icon: FileText },
  ];
  return (
    <div className="p-8 space-y-5">
      <div data-testid="adm-rel-subtabs" className="inline-flex items-center gap-1 rounded-full bg-secondary/40 border border-border p-1">
        {SUB.map((s) => {
          const Icon = s.icon;
          const active = view === s.id;
          return (
            <button
              key={s.id}
              data-testid={`adm-rel-sub-${s.id}`}
              onClick={() => setView(s.id)}
              className={`inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-full transition-colors ${
                active ? "bg-white border border-border shadow-sm text-foreground" : "text-foreground/60 hover:text-foreground"
              }`}
            >
              <Icon size={13}/>
              {s.label}
            </button>
          );
        })}
      </div>
      {view === "connections" && <ConnectionsView/>}
      {view === "invites"     && <InvitesView/>}
      {view === "tags"        && <TagsView/>}
      {view === "statements"  && <StatementEmailsView/>}
    </div>
  );
}


function ConnectionsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/connections/list?${p.toString()}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-rel-connections">
      <ListFilterBar
        q={q} setQ={setQ} onSearch={load}
        searchPlaceholder="Buyer or seller email/company"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="rel-conn-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending_seller_review">Pending seller review</option>
            <option value="declined">Declined</option>
            <option value="disconnected">Disconnected</option>
          </select>
        }
        statusCounts={data?.by_status}
        total={data?.total}
      />
      <DataTable
        loading={loading}
        rows={data?.rows || []}
        testidBase="adm-conn"
        columns={[
          { l: "Seller",   r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Buyer",    r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Tenant",   r: (x) => <span className="mono text-[11px] text-foreground/65">{x.tenant_slug || "—"}</span> },
          { l: "Status",   r: (x) => <StatusPill v={x.status}/> },
          { l: "Created",  r: (x) => <span className="text-[11px] text-foreground/55">{fmtAt(x.created_at)}</span> },
        ]}
        empty="No connections in this window."
      />
    </div>
  );
}


function InvitesView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [sinceDays, setSinceDays] = useState(180);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/invites/list?${p.toString()}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-rel-invites">
      <ListFilterBar
        q={q} setQ={setQ} onSearch={load}
        searchPlaceholder="Email, company, or note"
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="rel-inv-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="revoked">Revoked</option>
            <option value="expired">Expired</option>
          </select>
        }
        statusCounts={data?.by_status}
        total={data?.total}
      />
      <DataTable
        loading={loading}
        rows={data?.rows || []}
        testidBase="adm-inv"
        columns={[
          { l: "Recipient", r: (x) => <Stack top={x.email} sub={x.company || "—"}/> },
          { l: "Seller",    r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Tenant",    r: (x) => <span className="mono text-[11px] text-foreground/65">{x.tenant_slug || "—"}</span> },
          { l: "Terms",     r: (x) => <span className="text-[11px]">{x.default_terms || "—"}</span> },
          { l: "Status",    r: (x) => <StatusPill v={x.status}/> },
          { l: "Created",   r: (x) => <span className="text-[11px] text-foreground/55">{fmtAt(x.created_at)}</span> },
        ]}
        empty="No invites in this window."
      />
    </div>
  );
}


function TagsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (tag.trim()) p.set("tag", tag.trim());
      if (q.trim()) p.set("q", q.trim());
      const r = await api.get(`/admin/buyer-tags?${p.toString()}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4" data-testid="adm-rel-tags">
      <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
        <Input data-testid="rel-tag-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="Seller/buyer email or company" className="h-9 text-[13px] w-[260px]"/>
        <Input data-testid="rel-tag-filter" value={tag} onChange={(e) => setTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          placeholder="Tag (e.g. VIP)" className="h-9 text-[13px] w-[180px]"/>
        <button data-testid="rel-tag-refresh" onClick={load}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
          <RefreshCw size={13}/> Refresh
        </button>
        <div className="ml-auto text-[11px] text-foreground/55">
          {data?.total ?? 0} tag rows · {data?.all_tags?.length ?? 0} distinct tags
        </div>
      </div>

      {data?.all_tags?.length > 0 && (
        <div className="bg-white border border-border rounded-xl p-3" data-testid="rel-tag-cloud">
          <div className="text-[10px] uppercase tracking-wide text-foreground/55 mb-2">All tags in use</div>
          <div className="flex flex-wrap gap-1.5">
            {data.all_tags.map((t) => (
              <button key={t} onClick={() => { setTag(t); load(); }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary/60 border border-border text-[11px] hover:bg-secondary">
                <Tag size={10}/>{t}
              </button>
            ))}
          </div>
        </div>
      )}

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        testidBase="adm-tag"
        columns={[
          { l: "Seller", r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Buyer",  r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Tags",   r: (x) => (
            <div className="flex flex-wrap gap-1">
              {(x.tags || []).map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/60 border border-border text-[10.5px]">
                  <Tag size={9}/>{t}
                </span>
              ))}
              {(x.tags || []).length === 0 && <span className="text-[11px] text-foreground/40">—</span>}
            </div>
          )},
          { l: "Updated", r: (x) => <span className="text-[11px] text-foreground/55">{fmtAt(x.updated_at)}</span> },
        ]}
        empty="No tags applied yet."
      />
    </div>
  );
}


function StatementEmailsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [sinceDays, setSinceDays] = useState(90);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ since_days: String(sinceDays) });
      if (status) p.set("status", status);
      const r = await api.get(`/admin/statement-emails?${p.toString()}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sinceDays]);

  return (
    <div className="space-y-4" data-testid="adm-rel-statements">
      <ListFilterBar
        q="" setQ={() => {}} hideSearch onSearch={load}
        sinceDays={sinceDays} setSinceDays={setSinceDays}
        extra={
          <select data-testid="rel-stmt-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
            <option value="">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
        }
        statusCounts={data?.by_status}
        total={data?.total}
      />
      <DataTable
        loading={loading}
        rows={data?.rows || []}
        testidBase="adm-stmt"
        columns={[
          { l: "Seller",  r: (x) => <Stack top={x.seller_company || x.seller_email} sub={x.seller_email}/> },
          { l: "Buyer",   r: (x) => <Stack top={x.buyer_company || x.buyer_email} sub={x.buyer_email}/> },
          { l: "Period",  r: (x) => <span className="text-[11px]">{x.period_days}d</span> },
          { l: "Status",  r: (x) => <StatusPill v={x.status}/> },
          { l: "Resend",  r: (x) => <span className="mono text-[10.5px] text-foreground/60 truncate max-w-[180px] inline-block">{x.resend_id || x.error || "—"}</span> },
          { l: "Sent at", r: (x) => <span className="text-[11px] text-foreground/55">{fmtAt(x.sent_at)}</span> },
        ]}
        empty="No statement emails sent in this window."
      />
    </div>
  );
}


// ─── Reusable sub-components ────────────────────────────────────────────
function ListFilterBar({ q, setQ, onSearch, searchPlaceholder, sinceDays, setSinceDays, extra, statusCounts, total, hideSearch }) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-border rounded-xl p-3">
      {!hideSearch && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40"/>
          <Input data-testid="rel-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSearch(); }}
            placeholder={searchPlaceholder} className="h-9 pl-8 text-[13px] w-[280px]"/>
        </div>
      )}
      {extra}
      <select data-testid="rel-window" value={String(sinceDays)} onChange={(e) => setSinceDays(Number(e.target.value))}
        className="h-9 text-[12.5px] rounded-md border border-border bg-white px-2">
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="180">Last 6 months</option>
        <option value="365">Last 1 year</option>
      </select>
      <button data-testid="rel-refresh" onClick={onSearch}
        className="inline-flex items-center gap-1.5 text-[12.5px] font-medium border border-border rounded-md px-3 py-1.5 hover:bg-secondary">
        <RefreshCw size={13}/> Refresh
      </button>
      <div className="ml-auto flex items-center gap-3 text-[11px] text-foreground/55">
        <span>{total ?? 0} rows</span>
        {statusCounts && Object.keys(statusCounts).length > 0 && (
          <span className="flex items-center gap-1.5">
            {Object.entries(statusCounts).slice(0, 4).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-secondary/60 border border-border">
                <span className="capitalize">{k.replace(/_/g, " ")}</span><span className="font-medium">{v}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}


function DataTable({ loading, rows, columns, empty, testidBase }) {
  return (
    <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid={`${testidBase}-table`}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-widest text-foreground/55">
          <tr>{columns.map((c, i) => <th key={i} className="px-4 py-2.5 font-medium">{c.l}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55">Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="text-center py-12 text-foreground/55" data-testid={`${testidBase}-empty`}>{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id || i} data-testid={`${testidBase}-row-${(r.id || "").slice(0, 8)}`} className="hover:bg-secondary/20">
              {columns.map((c, j) => <td key={j} className="px-4 py-3">{c.r(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function Stack({ top, sub }) {
  return (
    <div className="leading-tight">
      <div className="text-[13px] font-medium">{top || "—"}</div>
      <div className="text-[11px] text-foreground/55">{sub || ""}</div>
    </div>
  );
}


function StatusPill({ v }) {
  const map = {
    active:                 "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending:                "bg-amber-50 text-amber-800 border-amber-200",
    pending_seller_review:  "bg-amber-50 text-amber-800 border-amber-200",
    accepted:               "bg-emerald-50 text-emerald-700 border-emerald-200",
    sent:                   "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed:                 "bg-rose-50 text-rose-700 border-rose-200",
    declined:               "bg-rose-50 text-rose-700 border-rose-200",
    revoked:                "bg-rose-50 text-rose-700 border-rose-200",
    disconnected:           "bg-slate-100 text-slate-700 border-slate-200",
    expired:                "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] border ${map[v] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
      {v || "—"}
    </span>
  );
}


function fmtAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ago = (Date.now() - d.getTime()) / 1000;
    if (ago < 60) return "just now";
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    if (ago < 604800) return `${Math.floor(ago / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}
